import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { Avatar, Box, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Popover, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getUiLanguage, localizedOrganizationName, tr } from '../lib/i18n';
import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { EMPLOYEE_TYPE } from '../lib/labels';
import { Button } from './button';

/** Stable, pleasant avatar color per person (Google-style colored initials). */
const AVATAR_COLORS = ['#1A73E8', '#188038', '#E37400', '#A142F4', '#D93025', '#12B5CB', '#C5221F', '#007B83', '#9334E6', '#B06000'];
export function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]!;
}

export function PersonAvatar({ name, seed, src, size = 32, themed = false }: { name?: string | null; seed?: string; src?: string | null; size?: number; themed?: boolean }) {
  const letter = name?.trim()?.[0] ?? tr('؟', '?');
  return (
    <Avatar
      src={src ?? undefined}
      alt={name ?? ''}
      sx={{ width: size, height: size, fontSize: size * 0.45, bgcolor: themed ? 'primary.main' : avatarColor(seed ?? name ?? ''), color: 'primary.contrastText' }}
    >
      {letter}
    </Avatar>
  );
}

/** Google account card: big avatar, greeting, "manage account", then org actions. */
export function AccountMenu({ onProfile, onSettings, onSubscription }: { onProfile: () => void; onSettings: () => void; onSubscription: () => void }) {
  const { me, logout, can } = useAuth();
  const m3 = useTheme().palette.m3;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  const firstName = me?.user.full_name?.split(' ')[0] ?? '';
  const organizationName = localizedOrganizationName(me?.organization);
  const run = (fn: () => void) => () => {
    close();
    fn();
  };

  return (
    <>
      <Tooltip
        title={
          <Box>
            <Box>{tr('حساب', 'Account')} {organizationName}</Box>
            <Box>{me?.user.full_name}</Box>
            <Box dir="ltr" sx={{ opacity: 0.8 }}>@{me?.user.username}</Box>
          </Box>
        }
      >
        <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label={tr('الحساب', 'Account')} sx={{ p: 0.75 }}>
          <PersonAvatar name={me?.user.full_name} seed={me?.user.id} src={me?.user.avatarUrl} size={34} themed />
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: 'calc(100vw - 16px)', borderRadius: '28px', bgcolor: m3.surfaceContainer, p: 1, mt: 0.5 } } }}
      >
        <Stack direction="row" alignItems="center" sx={{ px: 1.5, pt: 0.5 }}>
          <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', fontWeight: 500, color: m3.onSurfaceVariant }} noWrap>
            {organizationName}
          </Typography>
          <IconButton size="small" onClick={close} aria-label={tr('إغلاق', 'Close')} sx={{ position: 'absolute', insetInlineEnd: 12, top: 12 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack alignItems="center" spacing={1} sx={{ pt: 2.5, pb: 2.5, px: 2 }}>
          <PersonAvatar name={me?.user.full_name} seed={me?.user.id} src={me?.user.avatarUrl} size={80} themed />
          <Typography variant="h5" sx={{ fontWeight: 400, pt: 0.5 }}>{tr(`أهلاً ${firstName}!`, `Hi, ${firstName}!`)}</Typography>
          <Typography variant="body2" color="text.secondary" dir="ltr">@{me?.user.username}</Typography>
          {me?.user && (
            <Typography variant="caption" color="text.secondary">
              {me.user.is_owner ? tr('مالك الحساب', 'Account owner') : EMPLOYEE_TYPE[me.user.employee_type] ?? me.user.employee_type}
            </Typography>
          )}
          <Button variant="outlined" onClick={run(onProfile)} sx={{ mt: 1, bgcolor: 'transparent', borderColor: m3.outline, color: m3.primary }}>
            {tr('إدارة حسابك', 'Manage your account')}
          </Button>
        </Stack>

        <Box sx={{ bgcolor: m3.surfaceContainerLowest, borderRadius: '20px', overflow: 'hidden' }}>
          <List disablePadding>
            {can('org.settings') && (
              <ListItemButton onClick={run(onSettings)} sx={{ py: 1.5, px: 2.5 }}>
                <ListItemIcon><StorefrontOutlinedIcon /></ListItemIcon>
                <ListItemText primary={tr('إعدادات الشركة', 'Organization settings')} secondary={tr('الشعار واللون وروابط التواصل', 'Logo, color, and social links')} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} secondaryTypographyProps={{ variant: 'caption' }} />
              </ListItemButton>
            )}
            {can('org.settings') && (
              <ListItemButton onClick={run(onSubscription)} sx={{ py: 1.5, px: 2.5, borderTop: 1, borderColor: m3.surfaceContainer }}>
                <ListItemIcon><WorkspacePremiumOutlinedIcon /></ListItemIcon>
                <ListItemText primary={tr('الاشتراك', 'Subscription')} secondary={(getUiLanguage() === 'en' && me?.subscription?.planNameEn) || me?.subscription?.planName || tr('تفاصيل الباقة وتاريخ الانتهاء', 'Plan and expiration details')} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} secondaryTypographyProps={{ variant: 'caption' }} />
              </ListItemButton>
            )}
            <ListItemButton onClick={run(() => void logout())} sx={{ py: 1.5, px: 2.5, borderTop: can('org.settings') ? 1 : 0, borderColor: m3.surfaceContainer }}>
              <ListItemIcon><LogoutIcon /></ListItemIcon>
              <ListItemText primary={tr('تسجيل الخروج', 'Sign out')} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
            </ListItemButton>
          </List>
        </Box>
        <Typography variant="caption" component="div" sx={{ textAlign: 'center', color: m3.onSurfaceVariant, pt: 1.5, pb: 1 }}>
          FormTags
        </Typography>
      </Popover>
    </>
  );
}
